import { Heading, IconButton, Text, HStack, VStack } from '@genial/components';
import { Utils, useBreakpointValue } from '@genial/utils';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { ScrollView, View } from 'native-base';
import React, { useCallback } from 'react';

import {
  CustomerDetailStackParamList,
  SignedInNavigationScreenNavigationProp,
} from '../../../../../../../../routes/types';
import { usePrivatePensionAssetController } from '../../../../../../../BatchTicket/components/DynamicAsset/hooks/products/PrivatePension';
import { InfoPanel } from '../../../../../shared/InfoPanel';

import { PrivatePensionInvestCard } from './components/PrivatePensionInvestCard';

export const PrivatePensionApplication: React.FC = () => {
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();
  const route = useRoute<RouteProp<CustomerDetailStackParamList, 'PrivatePensionApplication'>>();

  const { offer, accountId, customerId, accountNumber, cpfCnpj } = route.params;

  const offerController = usePrivatePensionAssetController(offer, accountId, accountNumber);
  const {
    portabilities,
    beneficiaries,
    accountHolderForm,
    customerData,
    customerLoading,
    customerError,
    customerRefresher,
  } = offerController;

  const { handleSubmit } = accountHolderForm;
  const isMobile = useBreakpointValue({
    base: true,
    sm: false,
  });

  const handleEditPortability = useCallback(
    (index: number) => {
      navigation.navigate('CustomerDetailPrivatePensionPortability', {
        index,
        portability: portabilities[index],
        offer,
        accountId,
        customerId,
        accountNumber,
      });
    },
    [accountId, accountNumber, customerId, navigation, offer, portabilities]
  );

  const handleAddPortability = useCallback(() => {
    navigation.navigate('CustomerDetailPrivatePensionPortability', {
      index: undefined,
      portability: undefined,
      offer,
      accountId,
      customerId,
      accountNumber,
    });
  }, [accountId, accountNumber, customerId, navigation, offer]);

  const handleEditAccountHolder = useCallback(() => {
    navigation.navigate('CustomerDetailPrivatePensionAccountHolder', {
      offer,
      accountId,
      accountNumber,
      customerId,
    });
  }, [accountId, accountNumber, customerId, navigation, offer]);

  const handleEditBeneficiaries = useCallback(
    (index: number) => {
      navigation.navigate('CustomerDetailPrivatePensionBeneficiary', {
        index,
        beneficiary: beneficiaries[index],
        offer,
        accountId,
        accountNumber,
        customerId,
      });
    },
    [accountId, accountNumber, beneficiaries, customerId, navigation, offer]
  );

  const handleAddBeneficiary = useCallback(() => {
    navigation.navigate('CustomerDetailPrivatePensionBeneficiary', {
      index: undefined,
      beneficiary: undefined,
      offer,
      accountId,
      accountNumber,
      customerId,
    });
  }, [accountId, accountNumber, customerId, navigation, offer]);

  const handleClose = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const handleConfirm = handleSubmit(() => {
    navigation.navigate('CustomerDetailPrivatePensionOrderSummaryModal', {
      offer,
      accountId,
      accountNumber,
      customerId,
      cpfCnpj,
      cpfCnpjFormatted: Utils.Mask.CpfOrCnpj(cpfCnpj),
    });
  });

  return (
    <ScrollView flex={1} showsHorizontalScrollIndicator={false}>
      <View
        p="inset.xs"
        flex={1}
        width="full"
        maxWidth={{ base: '100%', sm: '95%', lg: '1280px' }}
        minWidth={0}>
        <VStack space="stack.xxxs">
          <HStack alignItems="center" justifyContent="space-between">
            <HStack alignItems="center" space="stack.nano">
              <IconButton
                icon="ArrowLeft"
                testId="return-button"
                shadow={isMobile ? undefined : 3}
                variant={isMobile ? 'unstyled' : 'link'}
                iconColor={isMobile ? 'neutral.dark' : 'brand.tertiary.principal'}
                iconSize="xs"
                padding={{ base: 0, sm: 'inset.nano' }}
                onPress={handleClose}
              />
              {isMobile ? (
                <Text fontSize="lg" variant="bold">
                  Nova Aplicação
                </Text>
              ) : (
                <Heading testId="customer-details-heading">Nova Aplicação</Heading>
              )}
            </HStack>
            {!!isMobile && (
              <InfoPanel
                isLoading={customerLoading}
                hasError={!!customerError}
                onRefresh={customerRefresher}
                customer={customerData}
                accountSelected={customerData?.additionalAccounts?.[0]}
              />
            )}
          </HStack>
          {!isMobile && (
            <InfoPanel
              isLoading={customerLoading}
              hasError={!!customerError}
              onRefresh={customerRefresher}
              customer={customerData}
              accountSelected={customerData?.additionalAccounts?.[0]}
            />
          )}

          <PrivatePensionInvestCard
            offer={offer}
            offerController={offerController}
            onEditPortability={handleEditPortability}
            onAddPortability={handleAddPortability}
            onEditAccountHolder={handleEditAccountHolder}
            onEditBeneficiaries={handleEditBeneficiaries}
            onAddBeneficiary={handleAddBeneficiary}
            onCancel={handleClose}
            onConfirm={handleConfirm}
          />
        </VStack>
      </View>
    </ScrollView>
  );
};
